/**
 * Screen 1g — field map.
 *
 * Wells are placed by real UTM coordinates, so panning and measuring work in
 * ground units rather than screen units. Structure contours are interpolated
 * from marker depths across the well set, which means the map redraws itself
 * when someone moves a top on the correlation screen.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { canWrite } from "../auth";
import { Icon } from "../components/icons";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import {
  Button,
  Checkbox,
  Chip,
  Dropdown,
  KeyValue,
  Loading,
  Notice,
  NumberField,
  Panel,
  Segmented,
  Slider,
  useToast,
} from "../components/ui";
import { useAsync, useElementSize, useMapView, useStored } from "../hooks";
import MapZoomControls from "../components/MapZoomControls";
import { ScreeningTable } from "../components/DataScope";
import {
  WELL_STATUS_LEGEND,
  applyView,
  contourAnchor,
  fitTransform,
  panBy,
  netPayControlPoints,
  placeLabels,
  structureContours,
  structureControlPoints,
  zoomAt,
  type MapTransform,
} from "../lib/basemap";
import { coordinateStep, coordinateTicks } from "../lib/ticks";
import type { MapPayload, MapWell, Project, ScreenedWell, Screening, Well } from "../types";

type Tool = "pan" | "zoom" | "measure" | "section" | "add-well";

const TOOLS: { key: Tool; label: string; icon: () => React.ReactNode }[] = [
  { key: "pan", label: "Geser", icon: () => Icon.pan({ size: 14 }) },
  { key: "zoom", label: "Zoom box", icon: () => Icon.zoomBox({ size: 14 }) },
  { key: "measure", label: "Ukur", icon: () => Icon.ruler({ size: 14 }) },
  { key: "section", label: "Draw traverse", icon: () => Icon.section({ size: 14 }) },
  { key: "add-well", label: "Add well", icon: () => Icon.plus({ size: 14 }) },
  // Dulu ada tombol "Grid" di sini. Ia tidak pernah dibaca di mana pun: memilihnya
  // hanya menyalakan sorotannya dan meninggalkan peta pada mode yang tidak
  // melakukan apa-apa. Kisi koordinat adalah sesuatu yang digambar peta, bukan
  // cara memakai tetikus, jadi tempatnya di daftar layer bersama yang lain.
];

/**
 * Kisi koordinat survei di atas peta lapangan.
 *
 * Yang berdiri di sini sebelumnya adalah jaring hiasan: dua gradien berulang
 * setiap 72 piksel, dipasang pada kotak petanya dan bukan pada petanya. Ia
 * tidak ikut bergeser ketika peta digeser dan tidak merapat ketika peta
 * diperbesar, sehingga satu-satunya hal yang bisa dibaca darinya adalah bahwa
 * layar ini punya garis-garis. Sebuah kisi yang tidak menyebut tempat lebih
 * buruk daripada tidak ada kisi, sebab bentuknya menjanjikan bahwa ia menyebut.
 *
 * Yang ini digambar lewat transformasi peta yang sama dengan sumur dan
 * konturnya, jadi ia menempel pada tanah: garis 512.000 mT tetap berada di
 * atas 512.000 mT ke mana pun peta dibawa.
 */
function CoordinateGrid({
  transform,
  width,
  height,
  opacity,
  crs,
}: {
  transform: MapTransform;
  width: number;
  height: number;
  opacity: number;
  crs: string;
}) {
  if (!(width > 0) || !(height > 0)) return null;

  const [west, north] = transform.toWorld(0, 0);
  const [east, south] = transform.toWorld(width, height);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;

  // Satu langkah untuk kedua sumbu. Peta ini setara skala — satu `scale` untuk
  // kedua arah — jadi kisi yang persegi adalah kisi yang jujur.
  const step = coordinateStep(Math.min(east - west, north - south), 4);
  const eastings = coordinateTicks(west, east, step);
  const northings = coordinateTicks(south, north, step);

  // Tepi peta ini sudah berpenghuni, dan angka yang tercetak di belakang panel
  // buram tidak terbaca sebagai angka. Palet alat memegang tepi kiri atas,
  // batang skala sudut kiri bawah, dan pembacaan koordinat sudut kanan bawah;
  // ketiganya digambar sesudah kisi ini dan akan menutupinya. Yang dilepas
  // hanya bilangannya — garis kisinya tetap melintas di bawah mereka.
  const TOOLS_BOTTOM = 190;
  const BAR_RIGHT = 180;
  const BAR_TOP = 44;
  const READOUT_LEFT = 200;

  const label = {
    font: "500 9.5px var(--font-mono)",
    fill: "var(--ink-3)",
    paintOrder: "stroke" as const,
    stroke: "var(--label-halo)",
    strokeWidth: 3,
  };

  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      opacity={opacity}
      aria-label={`Coordinate grid ${crs}`}
    >
      {eastings.map((value) => {
        const x = transform.toScreen(value, north)[0];
        const hidden = x < BAR_RIGHT || x > width - READOUT_LEFT;
        return (
          <g key={`e${value}`}>
            <line x1={x} x2={x} y1={0} y2={height} stroke="var(--grid-line)" strokeWidth={1} />
            {!hidden && (
              <text x={x} y={height - 7} textAnchor="middle" style={label}>
                {value.toFixed(0)}
              </text>
            )}
          </g>
        );
      })}
      {northings.map((value) => {
        const y = transform.toScreen(east, value)[1];
        const hidden = y < TOOLS_BOTTOM || y > height - BAR_TOP;
        return (
          <g key={`n${value}`}>
            <line x1={0} x2={width} y1={y} y2={y} stroke="var(--grid-line)" strokeWidth={1} />
            {!hidden && (
              <text x={7} y={y - 4} style={label}>
                {value.toFixed(0)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Layer names are derived from the project, not hardcoded: labelling every
 *  project's structure layer "Top Menggala" was fine for the demo field and
 *  actively misleading for any other. */
const layerList = (
  markerName: string,
  wellCount: number,
  unplacedCount: number,
  sectionCount: number,
  netPayMeta: string,
) => [
  {
    key: "structure",
    name: markerName ? `${markerName} structure` : "Surface structure",
    meta: markerName ? "contours" : "no markers",
    dot: "var(--accent)",
  },
  { key: "contours", name: "Depth contours", meta: "", dot: "var(--accent)" },
  { key: "licence", name: "Area outline", meta: "", dot: "var(--ink-4)" },
  {
    key: "wells",
    name: "Wells",
    // Rasio begitu ada sumur yang tak bisa ditaruh, dengan alasan yang sama
    // seperti keterangan lapisan net pay di bawahnya: angka yang berdiri
    // sendiri tidak mengatakan seberapa lengkap petanya.
    meta: unplacedCount
      ? `${wellCount} of ${wellCount + unplacedCount}`
      : `${wellCount} points`,
    dot: "var(--green)",
  },
  { key: "paths", name: "Deviated well tracks", meta: "from survey", dot: "var(--amber)" },
  {
    key: "section",
    name: "Correlation traverse",
    meta: sectionCount ? `${sectionCount} saved` : "none yet",
    dot: "var(--amber)",
  },
  { key: "netpay", name: "Net pay map", meta: netPayMeta, dot: "var(--violet)" },
  { key: "grid", name: "Coordinate grid", meta: "easting / northing", dot: "var(--ink-4)" },
];


/**
 * Kedua rel layar ini, masing-masing sebagai daftar tab yang sama bentuknya
 * dengan rel layar log — satu nama tiap rel, ditulis utuh sebagaimana ia
 * tertulis di kepala relnya ketika terbuka.
 */
const LAYER_TABS: RailTabItem<"layer">[] = [{ key: "layer", label: "Layer" }];
const INFO_TABS: RailTabItem<"sumur">[] = [{ key: "sumur", label: "Well" }];

export default function FieldMap({
  project,
  well,
  onWellChange,
}: {
  project: Project;
  well: Well | null;
  onWellChange: (id: number) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [box, size] = useElementSize<HTMLElement>();
  const [tool, setTool] = useState<Tool>("pan");
  const [surface, setSurface] = useState<string>("");
  // Zona yang dikonturkan peta net pay; kosong berarti total sumur. Tidak
  // disimpan, sama seperti `surface` di atasnya: keduanya menyebut nama yang
  // hanya ada di dalam satu proyek, dan nama yang dibawa ke proyek berikutnya
  // adalah nama yang tidak dikenal di sana.
  const [payZone, setPayZone] = useState<string>("");
  const [gridOpacity, setGridOpacity] = useStored("evolog.map.grid", 0.64);
  // Lebar kedua rel. Daftar layer di kiri hidup dengan lebar berapa pun;
  // panel sumur di kanan memuat tabel zona yang kolomnya terjepit pada 262
  // piksel, dan pada lapangan bernama panjang, nama sumurnya sendiri.
  // Kuncinya `…layers.width`, bukan `…layers`: yang kedua sudah dipakai
  // himpunan layer di bawah. Dua keadaan bertipe berbeda pada satu kunci
  // saling menimpa — mematikan sebuah layer menuliskan objek ke tempat
  // lebar rel disimpan, dan kunjungan berikutnya memakai objek itu sebagai
  // ukuran dalam piksel.
  const [layersWidth, setLayersWidth] = useStored("evolog.map.layers.width", 252);
  const [infoWidth, setInfoWidth] = useStored("evolog.map.info", 262);
  // Kedua rel peta melipat dengan pola yang sama seperti rel layar lain.
  // Layer dinyalakan sekali dan sumurnya dibaca sekali; yang dipandang
  // sesudahnya adalah petanya, dan petalah satu-satunya yang tumbuh ketika
  // 514 piksel di kiri dan kanannya dikembalikan kepadanya.
  const [layersCollapsed, setLayersCollapsed] = useStored("evolog.map.layers.collapsed", false);
  const [infoCollapsed, setInfoCollapsed] = useStored("evolog.map.info.collapsed", false);
  const [layers, setLayers] = useStored<Record<string, boolean>>("evolog.map.layers", {
    structure: true,
    contours: true,
    licence: true,
    wells: true,
    paths: true,
    section: true,
    netpay: false,
    grid: true,
  });
  const [sectionDraft, setSectionDraft] = useState<number[]>([]);
  // Jaring radius. Layar inilah satu-satunya tempat lingkarannya bisa dilihat
  // sebagai lingkaran, jadi di sini ia tidak hanya menandai daftar melainkan
  // menggambar dirinya di atas peta.
  const [centerWell, setCenterWell] = useStored<number | null>(
    "evolog.map.radius.center", null);
  const [radiusKm, setRadiusKm] = useStored("evolog.map.radius.km", 5);
  const [radiusMode, setRadiusMode] = useStored<"surface" | "trajectory">(
    "evolog.map.radius.mode", "trajectory");
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [measure, setMeasure] = useState<{ from: [number, number]; to: [number, number] } | null>(null);
  const measureStart = useRef<[number, number] | null>(null);

  const map = useAsync(
    (signal) => api.get<MapPayload>(`/api/projects/${project.id}/map/`, undefined, signal),
    [project.id],
  );

  const markerName = surface || map.data?.marker_names[0] || "";
  // Zona peta net pay, sebagaimana muatan peta mengenalnya. Nama yang tidak
  // ada dalam daftar jatuh kembali ke total: berpindah proyek meninggalkan
  // nama zona proyek sebelumnya di dalam keadaan, dan peta yang mengonturkan
  // nama yang tidak dikenal tidak menggambar apa pun tanpa mengatakan sebabnya.
  const zoneNames = map.data?.zone_names ?? [];
  const zoneName = zoneNames.includes(payZone) ? payZone : "";
  const wells = map.data?.wells ?? [];
  const located = wells.filter((w) => w.x !== null && w.y !== null);
  /**
   * Sumur yang tidak punya tempat, jadi tidak ada titiknya di peta ini.
   *
   * Dihitung dan disebutkan, bukan disaring diam-diam. Prabumulih memuat 1.451
   * sumur dan 1.191 di antaranya berkoordinat: 62 sumur Limau masih tanpa
   * letak, sebab kiriman pertamanya datang dengan `LONG .0` / `LATI .0` di
   * kepala LAS-nya dan tanpa satu baris pun di tabel headernya, dan kiriman
   * kedua hanya menutup sebagian. Lapisan yang hanya menulis "1191 points"
   * tidak dapat dibedakan dari peta yang lengkap, dan yang mencari L5A di sana
   * menyimpulkan petanya rusak alih-alih datanya yang tidak bertempat.
   */
  const unplaced = useMemo(
    () => wells.filter((w) => w.x === null || w.y === null)
      // Menurut abjad, bukan menurut urutan yang kebetulan dipulangkan server:
      // tiga nama yang berganti-ganti tiap kali peta dimuat tidak bisa dipakai
      // siapa pun untuk mengenali apa yang hilang.
      .map((w) => w.name)
      .sort((a, b) => a.localeCompare(b)),
    [wells],
  );
  /** Tiga nama pertama dan sisanya sebagai angka — cukup untuk mengenali
   *  lapangannya tanpa menumpahkan 157 nama ke dalam rel selebar 252 piksel.
   *  Daftar utuhnya menyusul di `title`: yang mencari satu sumur tertentu
   *  perlu jawaban ya/tidak, dan tiga nama tidak pernah memberikannya. */
  const unplacedNames = [
    unplaced.slice(0, 3).join(", "),
    unplaced.length > 3 ? ` and ${unplaced.length - 3} more` : "",
  ].join("");

  /**
   * Titik kendali peta net pay: sumur yang **sudah dihitung** dan berkoordinat.
   *
   * Berdiri di sini, sebelum daftar lapisan, sebab keterangan lapisannya
   * menyebut berapa sumur yang masuk — dan angka itulah yang memberi tahu
   * pembacanya seberapa kuat petanya.
   */
  const payControl = useMemo(() => netPayControlPoints(located, zoneName), [located, zoneName]);
  const screening = useAsync(
    (signal) =>
      api.get<Screening>(
        `/api/projects/${project.id}/screening/`,
        {
          center_well: centerWell,
          radius: Math.round(radiusKm * 1000),
          radius_mode: radiusMode,
        },
        signal,
      ),
    [project.id, centerWell, radiusKm, radiusMode],
    { skip: centerWell === null },
  );
  const keadaan = useMemo(() => {
    const rows = new Map<number, ScreenedWell>();
    for (const row of screening.data?.wells ?? []) rows.set(row.well_id, row);
    return rows;
  }, [screening.data]);
  /** Sumur pusatnya, sebagaimana peta mengenalnya — berikut jalur lubangnya. */
  const pusat = useMemo(
    () => wells.find((w) => w.id === centerWell) ?? null,
    [wells, centerWell],
  );
  // Keterangan lapisan net pay menyebut berapa sumur yang benar-benar punya
  // hasil hitungan — bukan berapa sumur yang ada. Peta yang dikonturkan dari
  // tiga sumur di antara enam belas adalah peta yang jauh lebih lemah daripada
  // yang terlihat.
  //
  // Nama zonanya tidak ikut ke sini melainkan tinggal di panel Net pay: baris
  // lapisan selebar rel 252 piksel, dan keterangan sepanjang "Menggala Sand A —
  // 5 dari 6 sumur" mendesak nama lapisannya sendiri sampai tinggal "N". Yang
  // pertama boleh hilang dari sebuah baris adalah keterangannya, bukan nama
  // barisnya.
  const LAYERS = layerList(
    markerName,
    located.length,
    unplaced.length,
    map.data?.sections.length ?? 0,
    payControl.length === 0
      ? "no computation yet"
      : `${payControl.length} of ${located.length} wells`,
  );

  // Alat kotak zum menyeret untuk memilih kotak, jadi seretannya tidak boleh
  // sekaligus menggeser peta: ambang tak terhingga mematikan geseran selama
  // alat itu aktif, tanpa mematikan roda tetikusnya.
  const zoom = useMapView({ dragThreshold: tool === "zoom" ? Number.POSITIVE_INFINITY : 4 });
  const [zoomBox, setZoomBox] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const zoomedByBox = useRef(false);
  const attachMap = useCallback(
    (el: HTMLElement | null) => {
      box(el);
      zoom.bind(el);
    },
    [box, zoom.bind],
  );

  /** World → screen. Padding keeps labels off the edge. */
  const fitted = useMemo(
    () => fitTransform(map.data?.extent, size.width, size.height),
    [map.data?.extent, size.width, size.height],
  );
  const transform = useMemo(() => applyView(fitted, zoom.view), [fitted, zoom.view]);

  /** Memperbesar sampai kotak yang barusan ditarik memenuhi kanvas. */
  const applyZoomBox = (box: { x0: number; y0: number; x1: number; y1: number }) => {
    const width = Math.abs(box.x1 - box.x0);
    const height = Math.abs(box.y1 - box.y0);
    // Kotak sekecil beberapa piksel adalah klik yang meleset, bukan permintaan
    // memperbesar delapan puluh kali.
    if (width < 10 || height < 10 || !size.width || !size.height) return;
    const cx = (box.x0 + box.x1) / 2;
    const cy = (box.y0 + box.y1) / 2;
    const factor = Math.min(size.width / width, size.height / height);
    zoom.setView((current) =>
      panBy(zoomAt(current, factor, cx, cy), size.width / 2 - cx, size.height / 2 - cy),
    );
    zoomedByBox.current = true;
  };

  /**
   * Structure contours from marker depths.
   *
   * The arithmetic lives in `lib/basemap` because the correlation screen draws
   * the same surface: two copies would be two maps free to disagree without
   * either being wrong.
   */
  const control = useMemo(
    () => structureControlPoints(located, markerName),
    [located, markerName],
  );
  const contours = useMemo(
    () => structureContours(control, transform, size.width, size.height),
    [control, transform, size.width, size.height],
  );

  /**
   * Kontur net pay — mesin yang sama dengan kontur struktur, titik kendali
   * yang berbeda.
   *
   * Bukan penggambar kedua: `structureContours` menerima titik `{x, y, z}` apa
   * pun, dan menyalinnya untuk ketebalan hanya akan melahirkan dua penjejak
   * kontur yang cepat atau lambat berselisih pada perkara yang tidak
   * kelihatan — interval yang dipilih, atau bagaimana sebuah sel diselesaikan.
   */
  const payContours = useMemo(
    () => structureContours(payControl, transform, size.width, size.height),
    [payControl, transform, size.width, size.height],
  );

  const sectionPath = useMemo(() => {
    const chosen = sectionDraft.length
      ? sectionDraft
      : (map.data?.sections[0]?.section_wells ?? []).map((entry) => entry.well);
    if (!transform || chosen.length < 2) return null;
    const points = chosen
      .map((id) => located.find((w) => w.id === id))
      .filter((w): w is MapWell => Boolean(w))
      .map((w) => transform.toScreen(w.x!, w.y!));
    if (points.length < 2) return null;
    return {
      d: `M${points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(" L")}`,
      start: points[0],
      end: points[points.length - 1],
      ids: chosen,
    };
  }, [sectionDraft, map.data, transform, located]);

  /**
   * Nama sumur siapa yang muat tanpa menimpa nama tetangganya.
   *
   * Aturannya di `lib/basemap`, bersama basemap korelasi dan peta jaring yang
   * lebih dulu memakainya. Yang diputuskan di sini hanya urutan berebutnya:
   * sumur yang sedang dibuka paling dulu, lalu yang sedang dirangkai menjadi
   * lintasan, lalu sisanya menurut urutan daftarnya — sebab keduanya yang
   * pertama adalah nama yang memang sedang dicari mata pemakainya, dan sebuah
   * nama yang kalah oleh tetangga yang tidak dipilih siapa pun adalah nama yang
   * hilang tepat ketika ia diperlukan.
   *
   * Titik sumurnya tidak ikut disaring: yang menghilang hanya tulisannya.
   */
  const labelled = useMemo(() => {
    if (!transform) return new Set<number>();
    const rank = (w: MapWell) =>
      w.id === well?.id ? 0 : sectionDraft.includes(w.id) ? 1 : 2;
    return placeLabels(
      [...located]
        .sort((a, b) => rank(a) - rank(b))
        .map((w) => ({ id: w.id, name: w.name, at: transform.toScreen(w.x!, w.y!) })),
      { width: size.width, height: size.height },
    );
  }, [located, transform, well?.id, sectionDraft, size.width, size.height]);

  const selected = wells.find((w) => w.id === well?.id) ?? null;
  const mayWrite = canWrite(project.my_role);

  const handleClick = (event: React.MouseEvent) => {
    // Melepas kotak zum juga melahirkan sebuah klik; ia bukan klik alat.
    if (zoomedByBox.current) {
      zoomedByBox.current = false;
      return;
    }
    if (!transform) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    if (tool === "measure") {
      const world = transform.toWorld(px, py);
      if (!measureStart.current) {
        measureStart.current = world;
        setMeasure(null);
      } else {
        setMeasure({ from: measureStart.current, to: world });
        measureStart.current = null;
      }
    }
  };

  const saveSection = async () => {
    if (sectionDraft.length < 2) return;
    try {
      await api.post("/api/sections/", {
        project: project.id,
        name: `Traverse ${new Date().toLocaleString("en-US")}`,
        datum_marker: markerName,
        well_ids: sectionDraft,
      });
      toast("Traverse saved.");
      setSectionDraft([]);
      map.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  const measureDistance = measure
    ? Math.hypot(measure.to[0] - measure.from[0], measure.to[1] - measure.from[1])
    : null;

  return (
    <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
      {/* Layers */}
      {layersCollapsed ? (
        <RailStrip
          side="left"
          tabs={LAYER_TABS}
          active="layer"
          onSelect={() => setLayersCollapsed(false)}
          expandLabel="Show the layer panel"
        />
      ) : (
        <aside
          style={{
            width: layersWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              // Menyusut dari 9 piksel: tombol lipat setinggi 22 piksel duduk di
              // baris ini, dan kepala rel yang tumbuh karenanya tidak lagi
              // sejajar dengan kepala rel di seberang peta.
              padding: "4px 7px 4px 12px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Layer</span>
            <RailCollapseButton
              side="left"
              collapsed={false}
              label="Hide the layer panel"
              onClick={() => setLayersCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
          >
            <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 3 }}>
              {LAYERS.map((layer) => (
                <Checkbox
                  key={layer.key}
                  checked={Boolean(layers[layer.key])}
                  onChange={(next) => setLayers({ ...layers, [layer.key]: next })}
                  label={layer.name}
                  dot={layer.dot}
                  meta={layer.key === "contours" && contours.length ? `interval ${contours[0].interval} m` : layer.meta}
                />
              ))}
            </div>

            {/* Sumur yang tidak punya tempat, disebutkan namanya.
                Sebuah lapisan yang menghitungnya saja ("1191 of 1451") sudah
                mengatakan bahwa petanya tidak lengkap, tetapi belum mengatakan
                siapa — dan yang membuka peta ini justru sedang mencari sumur
                tertentu. Tiga nama sudah cukup untuk mengenali lapangannya. */}
            {unplaced.length > 0 && (
              <div
                style={{
                  margin: "6px 12px 0",
                  paddingTop: 11,
                  borderTop: "1px solid var(--line-soft)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <span className="label">Not on the map</span>
                <span
                  title={unplaced.join(", ")}
                  style={{
                    font: "400 10.5px var(--font-sans)",
                    color: "var(--ink-3)",
                    lineHeight: 1.55,
                  }}
                >
                  {unplaced.length === 1
                    ? "1 well carries no surface coordinate, so the map cannot place it: "
                    : `${unplaced.length} wells carry no surface coordinate, so the map cannot place them: `}
                  {unplacedNames}.
                </span>
              </div>
            )}

            <div
              style={{
                margin: "6px 12px 0",
                paddingTop: 11,
                borderTop: "1px solid var(--line-soft)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span className="label">Well legend</span>
              {WELL_STATUS_LEGEND.map((entry) => (
                <div key={entry.name} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: entry.fill === "none" ? "transparent" : entry.fill,
                      border: "1.5px solid var(--ink-2)",
                      flex: "none",
                    }}
                  />
                  <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-3)" }}>{entry.name}</span>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: "auto",
                padding: "11px 12px",
                borderTop: "1px solid var(--line-soft)",
              }}
            >
              <Slider
                label="Coordinate grid weight"
                value={gridOpacity}
                min={0}
                max={1}
                onChange={setGridOpacity}
                format={(v) => `${Math.round(v * 100)}%`}
              />
            </div>
          </div>
        </aside>
      )}
      {!layersCollapsed && (
        <Resizer
          width={layersWidth}
          onChange={setLayersWidth}
          min={190}
          max={420}
          side="left"
          label="Resize the layer panel"
        />
      )}

      {/* Map canvas */}
      <section
        ref={attachMap}
        onClick={handleClick}
        onMouseDown={(event) => {
          if (tool !== "zoom" || event.button !== 0) return;
          const rect = event.currentTarget.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;
          setZoomBox({ x0: x, y0: y, x1: x, y1: y });
        }}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const px = event.clientX - rect.left;
          const py = event.clientY - rect.top;
          if (zoomBox) setZoomBox({ ...zoomBox, x1: px, y1: py });
          if (!transform) return;
          const [wx, wy] = transform.toWorld(px, py);
          setPointer({ x: wx, y: wy });
        }}
        onMouseUp={() => {
          if (!zoomBox) return;
          applyZoomBox(zoomBox);
          setZoomBox(null);
        }}
        onMouseLeave={() => {
          setPointer(null);
          setZoomBox(null);
        }}
        style={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          background: "var(--canvas-deep)",
          overflow: "hidden",
          touchAction: "none",
          cursor: zoom.panning
            ? "grabbing"
            : tool === "measure"
              ? "crosshair"
              : tool === "zoom"
                ? "crosshair"
                : tool === "pan"
                  ? "grab"
                  : "default",
        }}
      >
        <MapZoomControls
          zoom={zoom.view.zoom}
          home={zoom.home}
          onIn={() => zoom.zoomBy(1.4)}
          onOut={() => zoom.zoomBy(1 / 1.4)}
          onReset={zoom.reset}
          corner="under-compass"
        />
        {zoomBox && (
          <div
            style={{
              position: "absolute",
              left: Math.min(zoomBox.x0, zoomBox.x1),
              top: Math.min(zoomBox.y0, zoomBox.y1),
              width: Math.abs(zoomBox.x1 - zoomBox.x0),
              height: Math.abs(zoomBox.y1 - zoomBox.y0),
              border: "1px dashed var(--accent)",
              background: "var(--accent-zone)",
              pointerEvents: "none",
              zIndex: 3,
            }}
          />
        )}
        {layers.grid !== false && transform && (
          <CoordinateGrid
            transform={transform}
            width={size.width}
            height={size.height}
            opacity={gridOpacity}
            crs={project.crs}
          />
        )}

        {map.loading && <Loading label="Loading map…" />}
        {map.error && (
          <div style={{ padding: 16 }}>
            <Notice tone="error">{map.error}</Notice>
          </div>
        )}

        {transform && (
          <svg
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
            aria-label="Field map"
          >
            {/* Jangkauan jaring, digambar sebagai bentuk dan bukan sebagai
                angka. Layar inilah satu-satunya tempat sebuah radius bisa
                dilihat, dan yang dilihat orang di sini menjawab pertanyaan yang
                tidak dijawab tabel mana pun: apakah lingkarannya memang
                melingkari batuan yang dimaksud.

                Kedua modenya menggambar dua bentuk yang berbeda, sebab keduanya
                memang dua himpunan yang berbeda. Kepala sumur adalah lingkaran,
                dan itu persis ujinya. Ikut lintasan adalah jalur lubang yang
                ditebalkan sejauh radius — jumlah Minkowski-nya dengan sebuah
                cakram, yang digambar cukup dengan menebalkan garisnya dan
                membulatkan ujungnya. Bentuk itu jangkauannya **di semua
                kedalaman sekaligus**, sedangkan ujinya berlaku per TVDSS: yang
                tergambar karena itu sedikit lebih longgar daripada yang
                terjaring, dan keterangannya menyebut hal itu. */}
            {pusat && pusat.x !== null && pusat.y !== null && screening.data && (() => {
              const [cx, cy] = transform.toScreen(pusat.x, pusat.y);
              const tepi = transform.toScreen(pusat.x + screening.data.radius_m, pusat.y)[0];
              const r = Math.abs(tepi - cx);
              const jalur = pusat.path && pusat.path.length > 1 ? pusat.path : null;
              if (screening.data.mode === "trajectory" && jalur) {
                return (
                  <polyline
                    points={jalur.map(([x, y]) => transform.toScreen(x, y).join(",")).join(" ")}
                    fill="none"
                    stroke="var(--accent)"
                    strokeOpacity={0.1}
                    strokeWidth={r * 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              }
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="var(--accent)"
                  fillOpacity={0.08}
                  stroke="var(--accent)"
                  strokeOpacity={0.5}
                  strokeDasharray="4 4"
                />
              );
            })()}

            {/* Borehole tracks. This screen is the one wide enough to make them
                readable: on a two-hundred-pixel traverse map, a 1.4 km step-out
                across a 99 km field is three pixels. */}
            {layers.paths !== false &&
              located
                .filter((w) => w.deviated && (w.path?.length ?? 0) > 1)
                .map((w) => {
                  const points = w
                    .path!.map(([x, y]) => transform.toScreen(x, y))
                    .map(([sx, sy]) => `${sx},${sy}`)
                    .join(" ");
                  const end = transform.toScreen(
                    w.path![w.path!.length - 1][0],
                    w.path![w.path!.length - 1][1],
                  );
                  return (
                    <g key={`jalur-${w.id}`}>
                      <polyline
                        points={points}
                        fill="none"
                        stroke={w.panel_color || w.color}
                        strokeWidth={1.6}
                        strokeLinecap="round"
                        opacity={0.9}
                      />
                      <circle
                        cx={end[0]}
                        cy={end[1]}
                        r={3}
                        fill="none"
                        stroke={w.panel_color || w.color}
                        strokeWidth={1.4}
                      />
                    </g>
                  );
                })}
            {layers.contours &&
              contours.map((contour) => (
                <path
                  key={contour.level}
                  d={contour.d}
                  fill="none"
                  stroke="var(--accent)"
                  strokeOpacity={contour.opacity}
                  strokeWidth={1 + contour.opacity}
                />
              ))}
            {/* Net pay digambar dengan warna dan bentuk garis yang berbeda dari
                kontur struktur, dan itu bukan hiasan: keduanya boleh menyala
                bersamaan di atas peta yang sama, dan dua keluarga kontur
                berwarna sama pada satu peta tidak bisa dibaca sebagai dua
                besaran. Garis putus-putus untuk ketebalan adalah kelaziman
                peta isopach cetak.

                Violet, bukan amber: amber sudah dipakai lintasan korelasi —
                juga putus-putus — di peta yang sama, dan dua garis kuning
                putus-putus tidak bisa dibedakan sebagai dua hal. */}
            {layers.netpay &&
              payContours.map((contour) => (
                <path
                  key={contour.level}
                  d={contour.d}
                  fill="none"
                  stroke="var(--violet)"
                  strokeOpacity={contour.opacity}
                  strokeWidth={1 + contour.opacity}
                  strokeDasharray="7 4"
                />
              ))}
            {layers.licence && (
              <rect
                x={30}
                y={26}
                width={Math.max(size.width - 60, 0)}
                height={Math.max(size.height - 52, 0)}
                fill="none"
                stroke="var(--ink-7)"
                strokeWidth={1}
                strokeDasharray="10 6"
              />
            )}
            {layers.section && sectionPath && (
              <path
                d={sectionPath.d}
                fill="none"
                stroke="var(--amber)"
                strokeWidth={2}
                strokeDasharray="8 5"
              />
            )}
            {measure && (
              <line
                x1={transform.toScreen(...measure.from)[0]}
                y1={transform.toScreen(...measure.from)[1]}
                x2={transform.toScreen(...measure.to)[0]}
                y2={transform.toScreen(...measure.to)[1]}
                stroke="var(--accent-bright)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
            )}
          </svg>
        )}

        {/* Contour labels */}
        {transform &&
          layers.contours &&
          contours
            .filter((_, index) => index % 2 === 0)
            .map((contour) => {
              const anchor = contourAnchor(contour);
              if (!anchor) return null;
              const [px, py] = anchor;
              return (
                <span
                  key={contour.level}
                  style={{
                    position: "absolute",
                    left: px,
                    top: py,
                    padding: "1px 4px",
                    background: "var(--canvas-deep)",
                    font: "400 9.5px var(--font-mono)",
                    color: "var(--accent)",
                    pointerEvents: "none",
                  }}
                >
                  {contour.level.toFixed(0)}
                </span>
              );
            })}

        {/* Label kontur net pay. Bersatuan, tidak seperti label kontur
            struktur: peta yang memuat dua keluarga kontur mencetak dua deret
            angka, dan tanpa satuan tidak ada yang membedakan "40" yang berarti
            empat puluh meter di bawah laut dari "40" yang berarti empat puluh
            meter tebal. */}
        {transform &&
          layers.netpay &&
          payContours
            .filter((_, index) => index % 2 === 0)
            .map((contour) => {
              const anchor = contourAnchor(contour);
              if (!anchor) return null;
              const [px, py] = anchor;
              return (
                <span
                  key={contour.level}
                  style={{
                    position: "absolute",
                    left: px,
                    top: py,
                    padding: "1px 4px",
                    background: "var(--canvas-deep)",
                    font: "400 9.5px var(--font-mono)",
                    color: "var(--violet)",
                    pointerEvents: "none",
                  }}
                >
                  {`${contour.level.toFixed(0)} m`}
                </span>
              );
            })}

        {/* Wells */}
        {transform &&
          layers.wells &&
          located.map((mapWell) => {
            const [px, py] = transform.toScreen(mapWell.x!, mapWell.y!);
            const isSelected = mapWell.id === well?.id;
            const inSection = sectionDraft.includes(mapWell.id);
            // Diredupkan, tidak dihapus: sebuah peta yang kehilangan sumurnya
            // begitu radius disetel adalah peta yang berbohong tentang
            // lapangannya, dan yang di luar jaring justru sering yang ingin
            // dilihat orang berikutnya.
            const jaring = keadaan.get(mapWell.id);
            const diluar = jaring
              && (jaring.state === "outside radius" || jaring.state === "no coordinates");
            return (
              <button
                key={mapWell.id}
                type="button"
                title={`${mapWell.name} — ${mapWell.status_label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (tool === "section") {
                    setSectionDraft((current) =>
                      current.includes(mapWell.id)
                        ? current.filter((id) => id !== mapWell.id)
                        : [...current, mapWell.id],
                    );
                  } else {
                    onWellChange(mapWell.id);
                  }
                }}
                style={{
                  position: "absolute",
                  left: px,
                  top: py,
                  transform: "translate(-6px, -50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  opacity: diluar ? 0.35 : 1,
                }}
              >
                <span
                  style={{
                    width: isSelected ? 15 : 13,
                    height: isSelected ? 15 : 13,
                    borderRadius: "50%",
                    background: mapWell.color === "none" ? "transparent" : mapWell.color,
                    border: `2px solid ${isSelected ? "var(--accent)" : "var(--ink)"}`,
                    boxShadow: inSection
                      ? "0 0 0 3px var(--amber)"
                      : "0 0 0 3px rgba(13,16,20,.85)",
                    flex: "none",
                  }}
                />
                {labelled.has(mapWell.id) && (
                  <span
                    style={{
                      padding: "2px 6px",
                      background: "var(--panel)",
                      border: `1px solid ${isSelected ? "var(--accent)" : "var(--line)"}`,
                      borderRadius: 4,
                      font: "500 10px var(--font-mono)",
                      color: "var(--ink)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {mapWell.name}
                    {inSection && ` · ${sectionDraft.indexOf(mapWell.id) + 1}`}
                  </span>
                )}
              </button>
            );
          })}

        {sectionPath && layers.section && (
          <>
            <span style={tagStyle(sectionPath.start[0] - 16, sectionPath.start[1] - 22)}>A</span>
            <span style={tagStyle(sectionPath.end[0] + 10, sectionPath.end[1] - 22)}>A′</span>
          </>
        )}

        {/* Tool palette */}
        <div
          style={{
            position: "absolute",
            left: 12,
            top: 12,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: 5,
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius-panel)",
            boxShadow: "var(--shadow-float)",
          }}
        >
          {TOOLS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              title={entry.label}
              aria-pressed={tool === entry.key}
              onClick={(event) => {
                event.stopPropagation();
                setTool(entry.key);
                if (entry.key !== "measure") {
                  measureStart.current = null;
                  setMeasure(null);
                }
              }}
              style={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "var(--radius)",
                background: tool === entry.key ? "var(--accent-wash)" : "var(--control)",
                border: `1px solid ${tool === entry.key ? "var(--accent)" : "var(--border-control)"}`,
                color: tool === entry.key ? "var(--accent-pale)" : "var(--ink-2)",
                cursor: "pointer",
              }}
            >
              {entry.icon()}
            </button>
          ))}
        </div>

        {/* North arrow */}
        <div
          style={{
            position: "absolute",
            right: 14,
            top: 14,
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "50%",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" aria-label="Utara">
            <path d="M11 3 L14 13 L11 11 L8 13 Z" fill="var(--ink)" />
            <path d="M11 15 v4" stroke="var(--ink-6)" strokeWidth="1.2" />
          </svg>
        </div>

        {/* Scale bar */}
        {transform && (
          <div style={{ position: "absolute", left: 14, bottom: 14, display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ display: "flex", height: 7, width: 150, border: "1px solid var(--ink)" }}>
              <span style={{ flex: 1, background: "var(--ink)" }} />
              <span style={{ flex: 1 }} />
              <span style={{ flex: 1, background: "var(--ink)" }} />
              <span style={{ flex: 1 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", width: 150 }}>
              <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-3)" }}>0</span>
              <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-3)" }}>
                {(150 / transform.scale / 1000).toFixed(1)} km
              </span>
            </div>
          </div>
        )}

        {/* Coordinate readout */}
        <div
          style={{
            position: "absolute",
            right: 14,
            bottom: 14,
            display: "flex",
            gap: 14,
            padding: "5px 11px",
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius)",
            font: "400 10px var(--font-mono)",
          }}
        >
          <span style={{ color: "var(--ink-3)" }}>X {pointer ? pointer.x.toFixed(0) : "—"}</span>
          <span style={{ color: "var(--ink-3)" }}>Y {pointer ? pointer.y.toFixed(0) : "—"}</span>
          {measureDistance !== null && (
            <span style={{ color: "var(--accent)" }}>jarak {(measureDistance / 1000).toFixed(2)} km</span>
          )}
        </div>
      </section>
      {!infoCollapsed && (
        <Resizer
          width={infoWidth}
          onChange={setInfoWidth}
          min={220}
          max={520}
          label="Resize the well panel"
        />
      )}

      {/* Well info */}
      {infoCollapsed ? (
        <RailStrip
          tabs={INFO_TABS}
          active="sumur"
          onSelect={() => setInfoCollapsed(false)}
          expandLabel="Show the well panel"
        />
      ) : (
        <aside
          style={{
            width: infoWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Kepala rel, yang tidak ikut bergulir: daftar marker sebuah sumur
              bisa lebih panjang daripada layar. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 7px 4px 13px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Well</span>
            <RailCollapseButton
              side="right"
              collapsed={false}
              label="Hide the well panel"
              onClick={() => setInfoCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{
              flex: 1,
              padding: "14px 13px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <Panel title="Surface">
              <Dropdown
                value={markerName}
                options={(map.data?.marker_names ?? []).map((name) => ({ value: name, label: name }))}
                onChange={setSurface}
                width="100%"
              />
              <span style={{ font: "400 10px/1.4 var(--font-sans)", color: "var(--ink-5)" }}>
                {markerName
                  ? `Contours interpolated from marker depths in ${located.length} wells`
                  : "This project has no stratigraphic markers yet, so there is no surface to contour"}
                {contours.length > 0
                  ? ` (interval ${contours[0].interval} m, ${contours.length} lines).`
                  : "."}
              </span>
            </Panel>

            {/* Pemilih zona peta net pay, sebaris dengan pemilih permukaan di
                atasnya sebab keduanya menjawab pertanyaan yang sama bentuknya:
                lapisan mana yang sedang dikonturkan. Total sebuah sumur
                menjumlahkan seluruh zona yang ditembusnya, jadi peta total ikut
                menggambar kedalaman TD tiap sumur; peta satu zona tidak. */}
            <Panel title="Net pay">
              {zoneNames.length > 0 && (
                <Dropdown
                  value={zoneName}
                  options={[
                    { value: "", label: "Total — all zones" },
                    ...zoneNames.map((name) => ({ value: name, label: name })),
                  ]}
                  onChange={setPayZone}
                  width="100%"
                />
              )}
              <span style={{ font: "400 10px/1.4 var(--font-sans)", color: "var(--ink-5)" }}>
                {payControl.length === 0
                  ? zoneName
                    ? `No well with coordinates carries ${zoneName} in its computation, so there is nothing to contour.`
                    : "No well has been computed yet, so there is nothing to contour."
                  : `Contoured from ${payControl.length} of ${located.length} wells` +
                    // Sumur yang cukup tetapi tanpa satu garis pun bukan hal
                    // yang sama dengan sumur yang kurang, dan keduanya terlihat
                    // sama di atas peta: kosong. Zona yang net pay-nya nol di
                    // mana-mana adalah yang pertama.
                    (payContours.length > 0
                      ? ` (interval ${payContours[0].interval} m, ${payContours.length} lines).`
                      : payControl.length < 3
                        ? ", and three is the fewest that can be contoured."
                        : ", but no line can be drawn between their numbers.") +
                    " Wells not yet computed are dropped, not counted as zero."}
                {zoneNames.length === 0 &&
                  " A zone appears in this picker as soon as a deterministic run has computed it."}
              </span>
            </Panel>

            <Panel title="Radius filter">
              <Checkbox
                checked={centerWell !== null}
                label="Filter wells by radius"
                onChange={(on) =>
                  setCenterWell(on ? (centerWell ?? well?.id ?? located[0]?.id ?? null) : null)
                }
              />
              {centerWell !== null && (
                <>
                  {/* Tanpa `width="100%"`. Panel "Permukaan" di atas memakainya
                      dengan aman sebab dropdown-nya tidak berlabel; di sini
                      labelnya duduk sebaris di kirinya, jadi label ditambah
                      seratus persen lebarnya melampaui rel selebar 262 piksel —
                      dan yang melampaui rel terpotong dari tepi kiri, sehingga
                      panel ini terbaca "ARING RADIUS" dan "USAT". */}
                  <Dropdown
                    label="CENTRE"
                    value={centerWell}
                    options={located.map((w) => ({ value: w.id, label: w.name }))}
                    onChange={setCenterWell}
                  />
                  <NumberField
                    label="Radius"
                    unit="km"
                    value={radiusKm}
                    min={0.01}
                    step={0.5}
                    onChange={setRadiusKm}
                  />
                  <Segmented
                    value={radiusMode}
                    size="sm"
                    options={[
                      { value: "surface", label: "Well head" },
                      { value: "trajectory", label: "Along the track" },
                    ]}
                    onChange={(next) => setRadiusMode(next as "surface" | "trajectory")}
                  />
                  <span style={{ font: "400 10px/1.4 var(--font-sans)", color: "var(--ink-5)" }}>
                    {radiusMode === "trajectory"
                      ? "What is drawn is the reference borehole path thickened by the radius — its reach at every depth at once. The test itself works per TVDSS, so this shape is a little looser than what is really caught."
                      : "What is drawn is the circle itself, and that is exactly the test: the distance between well heads."}
                  </span>
                  {screening.error && <Notice tone="error">{screening.error}</Notice>}
                  {screening.data?.note && <Notice tone="warn">{screening.data.note}</Notice>}
                </>
              )}
            </Panel>

            {screening.data && screening.data.wells.length > 0 && (
              <Panel
                title={`Caught — ${screening.data.kept} of ${screening.data.asked} wells`}
                meta={`${radiusKm} km from ${screening.data.center_name}`}
              >
                {/* Dua ratus baris cukup: lapangan 1.402 sumur menghasilkan
                    2.804 baris DOM di dalam rel setinggi 260 piksel, dan yang
                    dibaca orang tetap yang teratas. Sisanya dihitung dan
                    disebut, bukan dihilangkan. */}
                <ScreeningTable screening={screening.data} maxRows={200} />
              </Panel>
            )}

            <Panel title="Selected well">
              {selected ? (
                <>
                  <KeyValue k="Wells" v={selected.name} mono={false} />
                  <KeyValue
                    k="X / Y"
                    v={`${selected.x?.toFixed(0) ?? "—"} / ${selected.y?.toFixed(0) ?? "—"}`}
                  />
                  <KeyValue k="TD depth" v={selected.td_md ? `${selected.td_md.toFixed(0)} m` : "—"} />
                  <KeyValue k="RT elevation" v={selected.kb_elevation ? `${selected.kb_elevation.toFixed(1)} m` : "—"} />
                  {selected.markers.map((marker) => (
                    <KeyValue key={marker.name} k={marker.name} v={`${marker.md.toFixed(1)} m`} />
                  ))}
                  <KeyValue k="Status" v={selected.status_label} mono={false} />
                </>
              ) : (
                <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
                  Click a well symbol on the map.
                </span>
              )}
            </Panel>

            <Panel title="Correlation traverse" meta={tool === "section" ? "mode tarik" : undefined}>
              {sectionDraft.length === 0 && (
                <span style={{ font: "400 10.5px/1.4 var(--font-sans)", color: "var(--ink-5)" }}>
                  Pick the “Draw traverse” tool, then click wells in order.
                </span>
              )}
              {sectionDraft.map((id, index) => {
                const entry = located.find((w) => w.id === id);
                return (
                  <div
                    key={id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 7px",
                      background: "var(--panel-inset)",
                      border: "1px solid var(--line-strong)",
                      borderRadius: 5,
                    }}
                  >
                    {Icon.grip({ size: 9, color: "var(--ink-6)" })}
                    <span style={{ font: "500 10.5px var(--font-sans)", color: "var(--ink-2)" }}>{entry?.name}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-7)" }}>{index + 1}</span>
                  </div>
                );
              })}
              {sectionDraft.length >= 2 && mayWrite && (
                <Button variant="primary" full onClick={saveSection}>
                  Save traverse ({sectionDraft.length} wells)
                </Button>
              )}
            </Panel>

            {map.data?.sections.length ? (
              <Panel title="Saved traverses">
                {map.data.sections.map((section) => (
                  <Chip key={section.id} onClick={() => navigate(`/projects/${project.id}/correlation`)}>
                    {section.name}
                  </Chip>
                ))}
              </Panel>
            ) : null}

            {located.length < 3 && (
              <Notice>
                Structure contours need at least three wells with coordinates sharing the same marker.
              </Notice>
            )}

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              <Button variant="primary" full onClick={() => navigate(`/projects/${project.id}/correlation`)}>
                Open the traverse in correlation
              </Button>
              <Button variant="ghost" full onClick={() => window.print()}>
                Ekspor peta (PDF)
              </Button>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}

function tagStyle(left: number, top: number): React.CSSProperties {
  return {
    position: "absolute",
    left,
    top,
    font: "600 11px var(--font-mono)",
    color: "var(--amber)",
    pointerEvents: "none",
  };
}
